

import React, { useEffect, useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { messageHandler } from "utils/messageHandler";
import {
  addressLine1Validation,
  cityValidation,
  companyNameValidation,
  countryValidation,
  emailValidation,
  firstNameValidation,
  lastNameValidation,
  officeNameValidation,
  phone2Validation,
  phoneValidation,
  stateValidation,
  statusValidation,
  zipcodeValidation,
} from "validationSchemas/common";
import { UIInput, UIReactSelect, UIInputDisable } from "components/Inputfields";
import {
  getCloudFileById,
  fileToBase64,
} from "components/Imageshelper";
import AddressAutocomplete from "components/AddressAutocomplete";
import Address_Details from "assets/images/Agents/Address_Details.png";
import BrokerageFirmHelpModal from "components/SignageBrokerageFirmToolTip";
import { getAuthCookie } from "../../utils/authCookies";
type FirmFromAPI = {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  phone1: string;
  phone2?: string;
  status: string; // "Active" | "Inactive" | ...
  firmDetails?: { companyName?: string };
  profilePic?: string | null;
  // address
  addressLine1: string;
  addressLine2: string;
  city: string;
  state: string;
  country: string;
  zipcode: string;
  cityCode: string;
  stateCode: string;
  countryCode: string;
  latitude: number;
  longitude: number;
  displaySrc: string | null;
};

const STATUS_OPTIONS = [
  { value: "Active", label: "Active" },
  { value: "Inactive", label: "Inactive" },
];
const US_COUNTRY_DEFAULTS = { country: "United States", countryCode: "US" } as const;

const toUrlString = (res: unknown): string | undefined => {
  if (!res) return undefined;
  if (typeof res === "string") return res;
  const r = res as any;
  const u = r?.url ?? r?.signedUrl ?? r?.data?.url;
  return typeof u === "string" ? u : undefined;
};

export default function SignageAddEditBrokerageFirm() {
  const { firmId } = useParams<{ firmId: string }>();
  const isEdit = Boolean(firmId);
  // console.log("isEdit", isEdit);
  const navigate = useNavigate();
  const location = useLocation();
  const token = getAuthCookie("sessionToken") || "";
  const userId = getAuthCookie("userId") || "";
  // Determine the base route based on current location
  const isSignageTeammember = location.pathname.includes("/signageteammember");
  const baseRoute = isSignageTeammember ? "/signageteammember" : "/signage";
  const [preview, setPreview] = useState<string | undefined>(undefined);
  const [loading, setLoading] = useState<boolean>(isEdit);
  const [formSubmitting, setFormSubmitting] = useState(false);
  const [cloudFileId, setCloudFileId] = useState<string | null>(null);
  const [openHelp, setOpenHelp] = useState(false);
  const [officeId, setOfficeId] = useState<string>("");
  const [defaultAddressId, setDefaultAddressId] = useState<string>("");
  const [serverImageSrc, setServerImageSrc] = useState<string | undefined>(
    undefined
  ); // ---------- upload helper (unchanged behavior) ----------
  const DEFAULT_TEN_ZEROS = "0000000000";
  const withDefault = (v?: string, def = DEFAULT_TEN_ZEROS) =>
    !v || v.trim() === "" || v === def ? def : v;

  const toCoordinate = (value: unknown) => {
    const coordinate = Number(value ?? 0);
    return Number.isFinite(coordinate) ? coordinate : 0;
  };

  // ---------- Formik ----------
  const formik = useFormik({
    enableReinitialize: false,
    initialValues: {
      photo: null as File | null,
      companyName: "",
      firstName: "",
      lastName: "",
      email: "",
      phone: DEFAULT_TEN_ZEROS,
      phone2: "",
      statuss: "Active", // Active | Inactive
      profilePic: null as string | null,
      // address
      officeName: "",
      officeType: "",
      officePhone: DEFAULT_TEN_ZEROS,
      addressLine1: "",
      addressLine2: "",
      city: "",
      state: "",
      country: US_COUNTRY_DEFAULTS.country,
      zipcode: "",
      cityCode: "",
      stateCode: "",
      countryCode: US_COUNTRY_DEFAULTS.countryCode,
      latitude: 0,
      longitude: 0,
      // profile
    },
    validationSchema: Yup.object({
      companyName: companyNameValidation,
      // firstName: firstNameValidation,
      // lastName: lastNameValidation,
      // email: emailValidation,
      // phone: phoneValidation,
      // statuss: statusValidation,
      // officeName: officeNameValidation,
      // officePhone: phone2Validation,
      // addressLine1: addressLine1Validation,
      // city: cityValidation,
      // state: stateValidation,
      // zipcode: zipcodeValidation,
      // country: countryValidation,
    }),
    onSubmit: async (values) => {
      try {
        setFormSubmitting(true);
        const profilePicForPayload =
          values.photo instanceof File ? await fileToBase64(values.photo) : "";

        const trimmedOfficeName = (values.officeName || "").trim();
        const includeOfficeInCreate = trimmedOfficeName.length > 0;
        const addressCountry = values.country?.trim() || US_COUNTRY_DEFAULTS.country;
        const addressCountryCode = values.countryCode?.trim() || US_COUNTRY_DEFAULTS.countryCode;
        const addressLatitude = toCoordinate(values.latitude);
        const addressLongitude = toCoordinate(values.longitude);

        const payload = {
          user: {
            email: values.email,
            password: "", // unchanged
            userType: "brokerage_firm",
            firstName: values.firstName,
            lastName: values.lastName,
            phone1: withDefault(values.phone),
            phone2: values.phone2 || "",
            ...(profilePicForPayload ? { profilePic: profilePicForPayload } : {}),
            licenseType: "BROKERAGE_FIRM",
            status: values.statuss,
            firmDetails: {
              companyName: values.companyName,
              companyWebsite: "https://example.com",
              address: {
                label: values.officeName,
                addressLine1: values.addressLine1,
                addressLine2: values.addressLine2,
                city: values.city,
                state: values.state,
                country: addressCountry,
                zip: values.zipcode,
                cityCode: values.cityCode,
                stateCode: values.stateCode,
                countryCode: addressCountryCode,
                latitude: addressLatitude,
                longitude: addressLongitude,
                phone: withDefault(values.phone),
              },
            },
          },
          ...(includeOfficeInCreate
            ? {
                office: {
                  officeName: trimmedOfficeName,
                  officeType: values.officeType || "Head Office",
                  status: true,
                  officeAddress: [
                    {
                      label: `${trimmedOfficeName} Address`,
                      addressLine1: values.addressLine1 || "",
                      addressLine2: values.addressLine2 || "",
                      city: values.city || "",
                      state: values.state || "",
                      country: addressCountry,
                      zip: values.zipcode || "",
                      phone: withDefault(values.officePhone),
                      latitude: addressLatitude,
                      longitude: addressLongitude,
                      cityCode: values.cityCode || "",
                      stateCode: values.stateCode || "",
                      countryCode: addressCountryCode,
                    },
                  ],
                },
              }
            : {}),
        };

        const payload1 = {
          email: values.email,
          password: "", // unchanged
          userType: "brokerage_firm",
          firstName: values.firstName,
          lastName: values.lastName,
          phone1: withDefault(values.phone),
          phone2: values.phone2 || "",
          ...(profilePicForPayload ? { profilePic: profilePicForPayload } : {}),
          firmDetails: {
            companyName: values.companyName,
            companyWebsite: "https://example.com",
            address: {
              label: values.officeName,
              addressLine1: values.addressLine1,
              addressLine2: values.addressLine2,
              city: values.city,
              state: values.state,
              country: addressCountry,
              zip: values.zipcode,
              cityCode: values.cityCode,
              stateCode: values.stateCode,
              countryCode: addressCountryCode,
              latitude: addressLatitude,
              longitude: addressLongitude,
              phone: withDefault(values.phone),
            },
            // companyLogo: uploadedUrl, // enable when you want to persist it
          },
          licenseType: "BROKERAGE_FIRM",
          licenseValidity: null,
          billingCycle: null,
          billingCyclePrice: 0,
          paymentMethod: null,
          status: values.statuss,
          modulePermissions: null,
        };
        const url = isEdit
          ? `${process.env.REACT_APP_API_URL}/users/${firmId}`
          : `${process.env.REACT_APP_API_URL}/brokerage_firm/create`;

        const method = isEdit ? "PATCH" : "POST";

        // 👇 choose payload based on mode
        const requestBody = isEdit ? payload1 : payload;

        const res = await fetch(url, {
          method,
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(requestBody),
        });
        if (!res.ok) {
          const errorData = await res.json();
          const errorMessage =
            errorData?.error || errorData?.message || "Failed to save data";
          throw new Error(errorMessage);
        }

        let brokerageRes: { id?: string; _id?: string; firmDetails?: { companyName?: string }; [key: string]: unknown } = {};
        try {
          brokerageRes = await res.json();
        } catch (_) {
          // empty or non-JSON body; use firmId in edit mode
        }
        const brokerageId = brokerageRes?.id ?? brokerageRes?._id ?? (isEdit && firmId ? firmId : undefined);

        const officePayload: any = {
          officeName: values.officeName || "",
          officeType: values.officeType || "",
          officeAddress: [
            {
              label: `${values.officeName} Address`,
              addressLine1: values.addressLine1 || "",
              addressLine2: values.addressLine2 || "",
              city: values.city || "",
              state: values.state || "",
              country: addressCountry,
              zip: values.zipcode || "",
              phone: values.officePhone || "",
              latitude: addressLatitude,
              longitude: addressLongitude,
              cityCode: values.cityCode || "",
              stateCode: values.stateCode || "",
              countryCode: addressCountryCode,
            },
          ],
        };

        if (!isEdit) {
          officePayload.created_by = {
            id: brokerageId ?? "",
            name: (brokerageRes?.firmDetails?.companyName as string) || "",
          };
          officePayload.status = true;
        }
        const hasOfficeId = Boolean(officeId?.trim());

        if (isEdit && hasOfficeId) {
          await createOffice(officePayload);
        }

        // Edit: update address by addressId (from GET /addresses/default/{user_id}). Create: set default address by user id.
        const addressPayload = {
          label: `${values.officeName} Address`,
          first_name: values.firstName,
          last_name: values.lastName,
          company: values.companyName,
          address_details: {
            label: `${values.officeName} Address`,
            addressLine1: values.addressLine1,
            addressLine2: values.addressLine2,
            city: values.city,
            state: values.state,
            zip: values.zipcode,
            country: addressCountry,
            phone: values.phone,
            cityCode: values.cityCode,
            stateCode: values.stateCode,
            countryCode: addressCountryCode,
            latitude: addressLatitude,
            longitude: addressLongitude,
          },
        };

        if (isEdit && defaultAddressId) {
          try {
            const addressResponse = await fetch(
              `${process.env.REACT_APP_API_URL}/addresses/${defaultAddressId}`,
              {
                method: "PUT",
                headers: {
                  "Content-Type": "application/json",
                  Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify(addressPayload),
              }
            );

            const addressResult: any = await addressResponse.json();
            if (!addressResponse.ok || addressResult?.error) {
              const message =
                addressResult?.error ||
                addressResult?.message ||
                "Failed to update address";
              console.error("Address API Error:", message);
              throw new Error(message);
            }
          } catch (error: any) {
            console.error("Address update failed:", error);
            throw error;
          }
        } else if (!isEdit && brokerageId) {
          try {
            const addressResponse = await fetch(
              `${process.env.REACT_APP_API_URL}/addresses/default/${brokerageId}`,
              {
                method: "PUT",
                headers: {
                  "Content-Type": "application/json",
                  Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify(addressPayload),
              }
            );

            const addressResult: any = await addressResponse.json();
            if (!addressResponse.ok || addressResult?.error) {
              const message =
                addressResult?.error ||
                addressResult?.message ||
                "Failed to set default address";
              console.error("Address API Error:", message);
              throw new Error(message);
            }
          } catch (error: any) {
            console.error("Default address update failed:", error);
            throw error;
          }
        }
        messageHandler.success("brokeragefirm", isEdit ? "update" : "create");
        navigate(`${baseRoute}/brokerageFirms`);
      } catch (e) {
        messageHandler.error("brokeragefirm", isEdit ? "update" : "create", e);
      } finally {
        setFormSubmitting(false);
      }
    },
  });

  const createOffice = async (data: any) => {
    if (!isEdit || !officeId?.trim()) return;

    const url = `${process.env.REACT_APP_API_URL}/offices/${officeId}`;
    try {
      const response = await fetch(url, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(data),
      });

      const created = await response.json();
      if (!response.ok || created?.error) {
        const msg = created?.error || created?.message || "Failed to save data";
        console.error("Office API Error:", msg);
      }
    } catch (error: any) {
      console.error("Office update failed:", error);
    }
  };

  //   const createOffice = async (data: any) => {
  //   if (isEdit && (!officeId || officeId.trim() === "")) {
  //     messageHandler.error("brokeragefirm", "update", new Error("Office not found. Unable to update office."));
  //     return;
  //   }
  //   const url = isEdit
  //     ? `${process.env.REACT_APP_API_URL}/offices/${officeId}`
  //     : "";
  //   try {
  //     const response = await fetch(url, {
  //       method: isEdit ? "PUT" : "POST",
  //       headers: {
  //         "Content-Type": "application/json",
  //         Authorization: `Bearer ${token}`,
  //       },
  //       body: JSON.stringify(data),
  //     });

  //     const created = await response.json();
  //     if (!response.ok || created?.error) {
  //       const msg = created?.error || created?.message || "Failed to save data";
  //       throw new Error(msg);
  //     }
  //   } catch (error: any) {
  //     messageHandler.error("brokeragefirm", isEdit ? "update" : "create", error);
  //   }
  // };



  // ---------- Fetch when editing ----------
  useEffect(() => {
    // console.log("isEdit useEffect triggered",isEdit);
    if (!isEdit) return;
    (async () => {
      try {
        const res = await fetch(
          `${process.env.REACT_APP_API_URL}/users/${firmId}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }
        );
        if (!res.ok) throw new Error("Failed to fetch firm");
        const data = await res.json();
        // Extract office data if available (prefer office data, fallback to firmDetails.address)
        const officeData = data?.office?.officeAddress?.[0] || data?.firmDetails?.address;
        setOfficeId(data?.offices?.[0]?._id);

        const officeIdjson = data?.offices?.[0]?._id;
        console.log(data?.offices?.[0]?._id);
         // Fetch default address to get addressId for later PUT /addresses/${addressId}
        if (officeIdjson) {
          console.log("Fetching default address for officeId:", officeIdjson);
          try {
            const addrRes = await fetch(
              `${process.env.REACT_APP_API_URL}/addresses/default/${officeIdjson}`,
              {
                method: "GET",
                headers: {
                  Authorization: `Bearer ${token}`,
                  "Content-Type": "application/json",
                },
              }
            );
            if (addrRes.ok) {
              const addrData = await addrRes.json();
              console.log("Fetched default address data:", addrData);
              setDefaultAddressId(addrData?._id ?? addrData?.id ?? "");
            }
          } catch (_) {
            // no default address yet
          }
        }

        formik.setValues({
          photo: null,
          companyName: data?.firmDetails?.companyName || "",
          firstName: data?.firstName || "",
          lastName: data?.lastName || "",
          email: data?.email || "",
          phone: data?.phone1 || "",
          phone2: data?.phone2 || "",
          statuss: data?.status || "Active",
          profilePic: data?.profilePic || null,
          officeName: data?.office?.officeName || data?.firmDetails?.address?.label || "",
          officeType: data?.office?.officeType || "",
          officePhone: officeData?.phone || "",
          addressLine1: officeData?.addressLine1 || "",
          addressLine2: officeData?.addressLine2 || "",
          city: officeData?.city || "",
          state: officeData?.state || "",
          country: officeData?.country || US_COUNTRY_DEFAULTS.country,
          zipcode: officeData?.zip || "",
          cityCode: officeData?.cityCode || "",
          stateCode: officeData?.stateCode || "",
          countryCode: officeData?.countryCode || US_COUNTRY_DEFAULTS.countryCode,
          latitude: toCoordinate(officeData?.latitude),
          longitude: toCoordinate(officeData?.longitude),
        });
      } catch (err) {
        console.log(err);
        messageHandler.error("brokeragefirm", "fetchSingle", err);
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line
  }, [isEdit, firmId, token]);

  const is24Hex = (s?: string | null) => !!s && /^[0-9a-f]{24}$/i.test(s);
  const normalizePath = (p: string) =>
    p.replace(/\\/g, "/").replace(/^\/+/, "");
  const buildAbsoluteFromRelative = (p: string) => {
    const base = (process.env.REACT_APP_BASE_URL || "").replace(/\/+$/, "");
    return `${base}/${normalizePath(p)}`;
  };
  useEffect(() => {
    let active = true;

    (async () => {
      const val = formik.values?.profilePic as string | undefined;
      if (!val) {
        if (active) setServerImageSrc(undefined);
        return;
      }

      if (/^https?:\/\//i.test(val)) {
        if (active) setServerImageSrc(val);
        return;
      }

      if (is24Hex(val)) {
        setCloudFileId(val);
        try {
          const res = await getCloudFileById(val, token as string);
          const u = toUrlString(res);
          if (active) setServerImageSrc(u);
        } catch {
          if (active) setServerImageSrc(undefined);
        }
        return;
      }

      // relative path
      if (active) setServerImageSrc(buildAbsoluteFromRelative(val));
    })();

    return () => {
      active = false;
    };
  }, [formik.values?.profilePic, token]);

  const joinUrl = (base?: string, path?: string) => {
    if (!path) return "";
    if (!base) return path;
    if (/^https?:\/\//i.test(path)) return path;
    const b = base.replace(/\/+$/, "");
    const p = path.replace(/^\/+/, "");
    return `${b}/${p}`;
  };
  const displaySrc =
    preview ??
    serverImageSrc ??
    // last resort: try to build from a relative profilePic
    (typeof formik.values.profilePic === "string" &&
      /^\/?uploads\//i.test(formik.values.profilePic)
      ? joinUrl(process.env.REACT_APP_BASE_URL, formik.values.profilePic)
      : null);
  useEffect(() => {
    return () => {
      if (preview) URL.revokeObjectURL(preview);
    };
  }, [preview]);

  const hasGoogleCoordinates =
    Number(formik.values.latitude || 0) !== 0 &&
    Number(formik.values.longitude || 0) !== 0;

  const clearAddressDetailErrors = () => {
    (["city", "state", "zipcode", "country"] as const).forEach((field) => {
      formik.setFieldTouched(field, false, false);
      formik.setFieldError(field, undefined);
    });
  };

  const clearGoogleAddressFields = () => {
    formik.setFieldValue("city", "");
    formik.setFieldValue("cityCode", "");
    formik.setFieldValue("state", "");
    formik.setFieldValue("stateCode", "");
    formik.setFieldValue("zipcode", "");
    formik.setFieldValue("country", US_COUNTRY_DEFAULTS.country);
    formik.setFieldValue("countryCode", US_COUNTRY_DEFAULTS.countryCode);
    formik.setFieldValue("latitude", 0);
    formik.setFieldValue("longitude", 0);
  };

  const ensureManualCountryDefaults = () => {
    if (hasGoogleCoordinates || formik.values.country?.trim()) return;
    formik.setFieldValue("country", US_COUNTRY_DEFAULTS.country, false);
    formik.setFieldValue("countryCode", US_COUNTRY_DEFAULTS.countryCode, false);
    formik.setFieldError("country", undefined);
  };

  const handleAddressLine1Change = (value: string) => {
    formik.setFieldValue("addressLine1", value);
    if (hasGoogleCoordinates || !value.trim()) {
      clearGoogleAddressFields();
      clearAddressDetailErrors();
    } else {
      ensureManualCountryDefaults();
    }
  };
  if (loading)
    return (
      <div className="text-center py-5 h-100">
        <div className="spinner-border text-primary"></div>
        <p className="mt-2 text-muted mb-0">Loading...</p>
      </div>
    );

  // ---------- UI ----------
  return (
    <div className="content container">
      <div className="row">
        <div className="col-md-12 d-flex justify-content-center">
          <div className="col-md-11 col-lg-112">
            <div className="container px-0 mb-3">
              <div className="d-flex align-items-center gap-2">
                <h3 className="m-0 fw-bold">
                  {isEdit ? "Edit Brokerage Firm" : "Add Brokerage Firm"}
                </h3>
                {/* Help Icon */}
                <button
                  onClick={() => setOpenHelp(true)}
                  className="d-inline-flex align-items-center justify-content-center p-0 border-0 bg-transparent"
                  style={{ cursor: "pointer" }}
                >
                  <span
                    className="d-inline-flex align-items-center justify-content-center"
                    style={{
                      width: 26,
                      height: 26,
                      borderRadius: "50%",
                      background: "#eef4ff",
                    }}
                  >
                    <i
                      className="bi bi-question-circle-fill"
                      style={{ fontSize: 18, color: "#155dfc" }}
                    ></i>
                  </span>
                </button>
                <BrokerageFirmHelpModal
                  open={openHelp}
                  onClose={() => setOpenHelp(false)}
                />
              </div>
              <div className="text-muted mt-1" style={{ fontSize: 14 }}>
                Create a new brokerage firm profile for your real estate
                business
              </div>
            </div>
            <div className="box-shadow shadow-border-3 rounded-4 mb-5">
              <form onSubmit={formik.handleSubmit} className="px-5 py-5">
                <div className=" d-flex justify-content-center">
                  <div className="rounded-4 border bg-white box-shadow-datatables p-3 p-md-4 mb-4 col-md-12 ">
                    <div className="d-flex align-items-center mb-5">
                      <span
                        className="d-inline-flex align-items-center justify-content-center"
                        style={{ width: 28, height: 28 }}
                      >
                        <i
                          className="bi bi-buildings-fill"
                          style={{ fontSize: 22, color: "#155dfc" }}
                        />
                      </span>
                      <div className="fs-19 roboto-new ms-2">
                        Brokerage Firm Details
                      </div>
                    </div>
                    <div className="row g-3">
                      <div className="col-md-4">
                        <UIInput
                          formik={formik}
                          name="companyName"
                          label="Company Name"
                          required
                          placeholder="Enter company name"
                        />
                      </div>
                      <div className="col-md-4">
                        <UIInput
                          formik={formik}
                          name="firstName"
                          label="First Name"
                          // required
                          placeholder="Enter first name"
                        />
                      </div>
                      <div className="col-md-4">
                        <UIInput
                          formik={formik}
                          name="lastName"
                          label="Last Name"
                          // required
                          placeholder="Enter last name"
                        />
                      </div>
                    </div>

                    {/* Row 2 */}
                    <div className="row g-3 mt-1">
                      <div className="col-md-4">
                        <UIInputDisable
                          formik={formik}
                          name="email"
                          label="Email"
                          // required
                          placeholder="Email address"
                          // disabled={isEdit}
                          autocomplete={!isEdit ? "off" : undefined}  
                        />
                      </div>
                      <div className="col-md-4">
                        <UIInput
                          formik={formik}
                          name="phone"
                          label="Phone"
                          // required
                          placeholder="Primary phone"
                        />
                      </div>
                      <div className="col-md-4">
                        <UIInput
                          formik={formik}
                          name="phone2"
                          label="Secondary Phone Number"
                          placeholder="Optional"
                        />
                      </div>
                    </div>

                    {/* Row 3: Upload (left) + Status (right) */}
                    <div className="row g-3 mt-1">
                      {/* Upload column */}
                      <div className="col-md-6">
                        <div className="form-label">Company Image Upload</div>
                        <label
                          htmlFor="photo"
                          className="d-inline-flex align-items-center justify-content-center mb-2"
                          style={{
                            width: 90,
                            height: 90,
                            border: "2px dashed #d0d5dd",
                            borderRadius: 12,
                            cursor: "pointer",
                          }}
                          title="Upload image"
                        >
                          {preview ?? serverImageSrc ? (
                            <img
                              src={(preview ?? serverImageSrc) as string}
                              alt="Preview"
                              style={{
                                width: "100%",
                                height: "100%",
                                objectFit: "contain",
                                borderRadius: 10,
                              }}
                            />
                          ) : (
                            <i className="bi bi-upload" />
                          )}
                        </label>

                        <input
                          id="photo"
                          name="photo"
                          type="file"
                          accept="image/*"
                          className="d-none"
                          onChange={(e) => {
                            const f = e.currentTarget.files?.[0] ?? null;
                            formik.setFieldValue("photo", f);
                            // update preview (revoke previous blob if any)
                            if (preview) URL.revokeObjectURL(preview);
                            setPreview(f ? URL.createObjectURL(f) : undefined);
                            // allow re-selecting same file
                            e.currentTarget.value = "";
                          }}
                          onBlur={formik.handleBlur}
                        />
                        <div>
                          <button
                            type="button"
                            className="btn upload-btn mt-2"
                            onClick={() =>
                              document.getElementById("photo")?.click()
                            }
                          >
                            Upload Image
                          </button>
                          <div
                            className="text-muted mt-2"
                            style={{ fontSize: 12 }}
                          >
                            JPG, PNG up to 10MB
                          </div>
                        </div>
                      </div>
                      {/* spacer so the row balances to 12 cols */}
                      <div className="col-md-2" />
                    </div>
                  </div>
                </div>
                <div className=" d-flex justify-content-center">
                  {/* Address */}
                  <div className="shadow-border-1 box-shadow-datatables p-4 bg-white mb-1">
                    <div className="mb-4">
                      <h6
                        className="mb-0 d-flex align-items-center order-tittle"
                        style={{ gap: 8 }}
                      >
                        <span
                          aria-hidden="true"
                          className="image-icons"
                          style={{
                            backgroundColor: "#ec4899",
                            WebkitMaskImage: `url(${Address_Details})`,
                            maskImage: `url(${Address_Details})`,
                          }}
                        />
                        <span>Address Details</span>
                      </h6>
                    </div>
                    <div className="row g-3">
                      <div className="col-md-6">
                        <UIInput
                          formik={formik}
                          name="officeName"
                          label="Office name"
                          // required
                          placeholder="Office name"
                        />
                      </div>
                      <div className="col-md-6">
                        <label className="form-label">
                          Address Line 1 
                          {/* <span className="text-danger">*</span> */}
                        </label>
                        <AddressAutocomplete
                          id="addressLine1"
                          name="addressLine1"
                          value={formik.values.addressLine1}
                          onChange={handleAddressLine1Change}
                          onSelect={(picked) => {
                            formik.setFieldValue(
                              "addressLine1",
                              picked.addressLine1
                            );
                            formik.setFieldValue("city", picked.city);
                            formik.setFieldValue("cityCode", picked.cityCode);
                            formik.setFieldValue("state", picked.state);
                            formik.setFieldValue("stateCode", picked.stateCode);
                            formik.setFieldValue("zipcode", picked.zipcode);
                            formik.setFieldValue("country", picked.country);
                            formik.setFieldValue(
                              "countryCode",
                              picked.countryCode
                            );
                            formik.setFieldValue("latitude", picked.latitude);
                            formik.setFieldValue("longitude", picked.longitude);
                            formik.setFieldTouched("addressLine1", false, false);
                            formik.setFieldError("addressLine1", undefined);
                            clearAddressDetailErrors();
                          }}
                          className={`form-control br-8 ${formik.touched.addressLine1 &&
                              formik.errors.addressLine1
                              ? "is-invalid"
                              : ""
                            }`}
                          placeholder="Enter address"
                          error={
                            formik.touched.addressLine1 &&
                              formik.errors.addressLine1
                              ? (formik.errors.addressLine1 as string)
                              : undefined
                          }
                          autoComplete="new-password"
                        />
                      </div>

                      <div className="col-md-6">
                        <UIInput
                          formik={formik}
                          name="addressLine2"
                          label="Address Line 2"
                          placeholder="Apartment, suite, etc. (optional)"
                        />
                      </div>
                      <div className="col-md-6"></div>

                      <div className="col-md-4">
                        <UIInputDisable
                          formik={formik}
                          name="city"
                          label="City"
                          // required
                          placeholder="City"
                          disabled
                          autocomplete="new-password"
                          autoCorrect="off"
                          spellCheck={false}
                        />
                      </div>
                      <div className="col-md-4">
                        <label className="form-label">State</label>
                        <input
                          type="text"
                          name="state"
                          className="form-control br-8"
                          placeholder="State"
                          value={formik.values.state}
                          disabled
                          autoComplete="new-password"
                          autoCorrect="off"
                          spellCheck={false}
                        />
                        {formik.touched.state && formik.errors.state && (
                          <div className="invalid-feedback d-block">{formik.errors.state as string}</div>
                        )}
                      </div>
                      <div className="col-md-4">
                        <UIInputDisable
                          formik={formik}
                          name="zipcode"
                          label="Zip Code"
                          // required
                          placeholder="Zip"
                          disabled
                          autocomplete="new-password"
                          autoCorrect="off"
                          spellCheck={false}
                        />
                      </div>
                      <div className="col-md-4">
                        <UIInputDisable
                          formik={formik}
                          name="country"
                          label="Country"
                          // required
                          placeholder="United States"
                          disabled
                          autocomplete="new-password"
                          autoCorrect="off"
                          spellCheck={false}
                        />
                      </div>
                    </div>
                  </div>
                </div>
                <div className="row g-3 mt-3 ms-3">
                  <div className="col-md-4">
                    <UIReactSelect
                      formik={formik}
                      name="statuss"
                      label="Status"
                      // required
                      options={[
                        { value: "Active", label: "Active" },
                        { value: "Inactive", label: "Inactive" },
                      ]}
                      placeholder="Select status"
                    />
                  </div>
                </div>
                <div className="d-flex justify-content-end mt-4">
                  <div className="d-flex justify-content-between align-items-center">
                    <div className="me-3">
                      <button
                        type="button"
                        onClick={() => navigate(-1)}
                        className="btn back-btn surface-btn fw-bold fs-16"
                      >
                        <i className="bi bi-chevron-left me-2" />
                        Back
                      </button>
                    </div>
                    <button
                      type="submit"
                      className="btn btn-primary save-btn surface-btn fw-bold fs-16"
                      disabled={formSubmitting}
                    >
                      {formSubmitting ? "Submitting..." : "Save"}
                    </button>
                  </div>

                  {/* <div className="d-flex justify-content-between align-items-center w-100">
                                            <button
                                                type="button"
                                                className="btn btn-outline-secondary br-8"
                                                onClick={() => navigate(-1)}
                                            >
                                                Cancel
                                            </button>
                                            <button
                                                type="submit"
                                                className="btn btn-primary"
                                                disabled={formSubmitting}
                                            >
                                                {formSubmitting ? "Saving..." : "Save"}
                                            </button>
                                        </div> */}
                </div>
              </form>
            </div>
          </div>
          {/* /Body */}
        </div>
        {/* /Outer card */}
      </div>
    </div>
  );
}
